/**
 * Recipe Analysis Module
 * 
 * Handles recipe analysis, cleanup, substitutions, and unit conversions.
 * Extracted from recipeService.ts for better modularity.
 */

import { RecipeFormData } from '../../types/recipe';
import { AssistantResponse } from '@/types/assistantTypes';
import { UserDietaryPreferences } from '@/types/assistant';
import { PROMPT_TEMPLATES } from '@/prompts/recipePrompts';
import { generateContent } from '../geminiService';
import { cleanupRecipeMarkdown as geminiCleanupRecipeMarkdown, GeminiResponse } from '../api/geminiApiService';
import { generateIntentDetection } from '../ai/aiGateway';
import { modelService } from '../modelService';

/**
 * Analyzes the current recipe for cleanup suggestions.
 * This function would use the RECIPE_CLEANUP prompt template.
 * For now, it's a placeholder.
 */
export async function analyzeRecipeForCleanup(recipeMarkdown: string): Promise<AssistantResponse> {
    if (!recipeMarkdown) {
        return { success: false, error: "Recipe markdown content is required." };
    }

    const prompt = PROMPT_TEMPLATES.RECIPE_CLEANUP(recipeMarkdown);
    const model = await modelService.getModelForFeature('modification' as any);
    const result = await generateContent(prompt, model);

    if (result.success && result.text) {
        // Here, you might want to parse the result.text further if Gemini
        // is expected to return structured data (e.g., suggestions list and cleaned markdown separately)
        return { success: true, message: "Recipe analysis complete.", data: result.text.trim() };
    } else {
        return { success: false, error: result.error || "Failed to analyze recipe for cleanup." };
    }
}

/**
 * Suggests substitutions for a given ingredient in the context of a recipe.
 */
export async function suggestSubstitutions(
    ingredient: string,
    recipeTitle: string,
    dietaryPreferences?: UserDietaryPreferences
): Promise<AssistantResponse> {
    if (!ingredient || !recipeTitle) {
        return { success: false, error: "Ingredient and recipe title are required." };
    }

    let restrictionsString = null;
    if (dietaryPreferences) {
        restrictionsString = Object.entries(dietaryPreferences)
            .filter(([, value]) => value)
            .map(([key]) => key)
            .join(', ');
    }

    const prompt = PROMPT_TEMPLATES.SUBSTITUTIONS(ingredient, restrictionsString, recipeTitle);
    const model = await modelService.getModelForFeature('modification' as any);
    const result = await generateContent(prompt, model);

    if (result.success && result.text) {
        return { success: true, message: "Substitution suggestions generated.", data: result.text.trim() };
    } else {
        return { success: false, error: result.error || "Failed to generate substitutions." };
    }
}

/**
 * Orchestrates the recipe cleanup process.
 * Calls the geminiApiService to clean the markdown and returns the result.
 * @param recipeText The raw recipe markdown text.
 * @returns A promise that resolves to an AssistantResponse.
 */
export async function handleRecipeCleanupRequest(recipeText: string): Promise<AssistantResponse> {
    if (!recipeText) {
        return { success: false, error: "Recipe text is required for cleanup." };
    }


    const result: GeminiResponse = await geminiCleanupRecipeMarkdown(recipeText);

    if (result.success && result.text) {

        let cleanedText = result.text;

        const listNormalizationRegex = /(\n\s*[-*+]\s[^\n]+)\n\s*\n+(\s*[-*+]\s)/g;
        cleanedText = cleanedText.replace(listNormalizationRegex, '$1\n$2');

        cleanedText = cleanedText.replace(/^\s*\n+/, '');
        cleanedText = cleanedText.replace(/\n\s*\n+$/, '\n');

        if (cleanedText.trim() === "") {
            cleanedText = "";
        }

        return { success: true, message: "Recipe cleaned successfully and lists normalized.", data: cleanedText };
    } else {
        return {
            success: false,
            error: result.error || "Failed to clean up recipe markdown in assistantService.",
            data: result.debugInfo,
        };
    }
}

/**
 * Converts measurements to the target system (metric/imperial).
 */
export async function convertUnits(
    measurements: string,
    targetSystem: 'metric' | 'imperial'
): Promise<AssistantResponse> {
    if (!measurements || !targetSystem) {
        return { success: false, error: "Measurements and target system are required." };
    }

    const prompt = PROMPT_TEMPLATES.UNIT_CONVERSION(measurements, targetSystem);
    const model = await modelService.getModelForFeature('modification' as any);
    const result = await generateContent(prompt, model);

    if (result.success && result.text) {
        return { success: true, message: "Unit conversion complete.", data: result.text.trim() };
    } else {
        return { success: false, error: result.error || "Failed to convert units." };
    }
}

/**
 * Detects user intent for studio assistant messages
 */
export async function detectUserIntent(
    message: string,
    recipe: RecipeFormData
): Promise<'MODIFY' | 'QUESTION' | 'CHAT' | 'UNCLEAR'> {
    try {
        // Enhanced intent detection for substitution questions
        const lowerMessage = message.toLowerCase();

        // Keywords that indicate modification vs questions
        const modificationKeywords = ['make', 'change', 'replace', 'substitute', 'instead of', 'swap', 'alter', 'modify', 'add', 'remove', 'take out', 'use instead', 'instead use'];
        const questionKeywords = ['what', 'how', 'can i', 'options', 'alternatives', 'suggestions', 'ideas', 'recommend', 'tell me', 'explain'];

        // Check if it's clearly a question about alternatives
        const isSubstitutionQuestion = lowerMessage.includes('instead') && questionKeywords.some(kw => lowerMessage.includes(kw));
        const isQuestion = questionKeywords.some(kw => lowerMessage.includes(kw)) && !modificationKeywords.some(kw => lowerMessage.includes(kw));

        if (isSubstitutionQuestion || isQuestion) {
            return 'QUESTION';
        }

        // Use AI detection for edge cases
        const prompt = PROMPT_TEMPLATES.INTENT_DETECTION(message, recipe.title || 'Untitled Recipe');
        const result = await generateIntentDetection(prompt, 'detectUserIntent');

        if (result.success && result.text) {
            const intent = result.text.trim().toUpperCase();
            if (['MODIFY', 'QUESTION', 'CHAT', 'UNCLEAR'].includes(intent)) {
                return intent as 'MODIFY' | 'QUESTION' | 'CHAT' | 'UNCLEAR';
            }
        }

        return 'QUESTION'; // Default to QUESTION for unclear cases
    } catch {
        return 'QUESTION';
    }
}
